// Les dépenses du mois et les économies qu'on peut y trouver en les examinant.
import type { TextKey } from './fr';
import { fmtEur } from '../engine/format';
import type { ExpenseId, GameState } from '../engine/state';
import { cancelForgotten, forgottenTotal } from '../game/subscriptions';

export interface HuntOption {
  id: string;
  label: TextKey;
  /** Nouveau montant mensuel à partir du montant actuel… */
  apply?: (current: number) => number;
  /** …ou un changement de la partie, pour une dépense faite de plusieurs éléments (les abonnements). */
  applyTo?: (state: GameState) => void;
  /** Les montants cités par le texte de l'option. */
  params?: (state: GameState) => Record<string, string>;
  available?: (state: GameState) => boolean;
  wellbeing?: number;
}

export interface ExpenseDef {
  label: TextKey;
  hunt?: { clicks: number; options: HuntOption[] };
}

export const EXPENSES: Record<ExpenseId, ExpenseDef> = {
  logement: { label: 'expense.logement' },
  taxeFonciere: { label: 'expense.taxeFonciere' },
  copropriete: { label: 'expense.copropriete' },
  charges: { label: 'expense.charges' },
  energie: {
    label: 'expense.energie',
    hunt: {
      clicks: 3,
      options: [
        { id: 'offre', label: 'hunt.energie.offre', apply: (current) => current * 0.9 },
        { id: 'sobriete', label: 'hunt.energie.sobriete', apply: (current) => current * 0.85, wellbeing: -1 },
      ],
    },
  },
  courses: {
    label: 'expense.courses',
    hunt: {
      clicks: 4,
      options: [
        {
          id: 'plan',
          label: 'hunt.courses.plan',
          apply: (current) => Math.max(150, current - 40),
          wellbeing: -1,
        },
      ],
    },
  },
  personnel: {
    label: 'expense.personnel',
    hunt: {
      clicks: 2,
      options: [
        { id: 'occasion', label: 'hunt.personnel.occasion', apply: (current) => Math.max(30, current * 0.75) },
        {
          id: 'coiffeur',
          label: 'hunt.personnel.coiffeur',
          apply: (current) => Math.max(30, current - 10),
          wellbeing: -1,
        },
      ],
    },
  },
  transport: {
    label: 'expense.transport',
    hunt: {
      clicks: 2,
      options: [
        {
          id: 'bike',
          label: 'hunt.transport.bike',
          apply: (current) => Math.max(10, current - 30),
          available: (state) => state.hasBike,
          wellbeing: 1,
        },
        { id: 'annual', label: 'hunt.transport.annual', apply: (current) => current * 0.9 },
      ],
    },
  },
  forfait: {
    label: 'expense.forfait',
    hunt: {
      clicks: 2,
      options: [{ id: 'cheap', label: 'hunt.forfait.cheap', apply: (current) => Math.min(current, 10) }],
    },
  },
  abonnements: {
    label: 'expense.abonnements',
    hunt: {
      clicks: 3,
      options: [
        {
          id: 'cut',
          label: 'hunt.abonnements.cut',
          available: (state) => forgottenTotal(state) > 0,
          params: (state) => ({ amount: fmtEur(forgottenTotal(state)) }),
          applyTo: (state) => {
            cancelForgotten(state);
          },
        },
      ],
    },
  },
  assurance: {
    label: 'expense.assurance',
    hunt: {
      clicks: 3,
      options: [{ id: 'compare', label: 'hunt.assurance.compare', apply: (current) => current * 0.6 }],
    },
  },
  banque: {
    label: 'expense.banque',
    hunt: {
      clicks: 2,
      options: [{ id: 'online', label: 'hunt.banque.online', apply: () => 0 }],
    },
  },
  assuranceAuto: {
    label: 'expense.assuranceAuto',
    hunt: {
      clicks: 3,
      options: [{ id: 'compare', label: 'hunt.assuranceAuto.compare', apply: (current) => current * 0.75 }],
    },
  },
  // Pas de chasse aux économies sur la mutuelle : on change de formule, et ce qu'elle rembourse suit.
  mutuelle: { label: 'expense.mutuelle' },
  carburant: {
    label: 'expense.carburant',
    hunt: {
      clicks: 2,
      options: [{ id: 'drive', label: 'hunt.carburant.drive', apply: (current) => current * 0.85 }],
    },
  },
};

/** Ordre d'affichage des dépenses. */
export const EXPENSE_ORDER: ExpenseId[] = [
  'logement',
  'taxeFonciere',
  'copropriete',
  'charges',
  'energie',
  'assurance',
  'mutuelle',
  'courses',
  'personnel',
  'transport',
  'assuranceAuto',
  'carburant',
  'forfait',
  'abonnements',
  'banque',
];
